import { useId, useState } from "react";
import type { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faLock } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

import { useLanguage } from "../../../context/LanguageProvider";
import { toFarsiNumber } from "../../../shared/utils/commonUtils";

interface SectionBarProps {
    title: string;
    rank: number; // Position of the section (1-based)
    children: ReactNode; // The section's items
    enabled?: boolean; // Locked sections can't be opened
}

// Collapsible section header with its items underneath
const SectionBar = ({ title, rank, children, enabled = true }: SectionBarProps) => {
    const { lang } = useLanguage();
    const panelId = useId();
    const [isOpen, setIsOpen] = useState(false);

    const showOpen = enabled && isOpen;

    return (
        <>
            {/* A real button: keyboard users can open sections and screen readers announce the state */}
            <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                disabled={!enabled}
                aria-expanded={showOpen}
                aria-controls={panelId}
                className={classNames(
                    "relative mt-8 w-full text-start border-4 border-gray-200 p-4 lg:p-8 flex justify-between items-center",
                    showOpen ? "rounded-t-lg" : "rounded-lg",
                    enabled
                        ? "cursor-pointer bg-gray-100 hover:bg-gray-200 transition"
                        : "cursor-not-allowed bg-gray-300 opacity-50"
                )}
            >
                <div className="flex items-center gap-x-3">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 lg:text-xl text-lg font-bold bg-gray-200 rounded-full flex items-center justify-center">
                        <span>{lang === "fa" ? toFarsiNumber(rank) : rank}</span>
                    </div>

                    <h2 className="font-bold text-2xl mr-5">{title}</h2>
                </div>

                {enabled ? (
                    <FontAwesomeIcon
                        icon={faChevronLeft}
                        className={classNames(
                            "transition h-4",
                            showOpen ? "-rotate-90" : "lg:h-6"
                        )}
                    />
                ) : (
                    <FontAwesomeIcon icon={faLock} className="h-6" />
                )}
            </button>

            {enabled && (
                <div
                    id={panelId}
                    hidden={!isOpen}
                    className="w-full bg-gray-100 border-4 rounded-b-lg transition"
                >
                    {children}
                </div>
            )}
        </>
    );
};

export default SectionBar;